import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Spin,
  Empty,
  Typography,
  Select,
  Space,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  MailOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  fetchSequenceStats,
  fetchSequenceUsers,
} from "../../store/actions/emailAnalyticsActions";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

const EmailSequenceSection = ({
  sequence,
  sequenceUsers,
  loading,
  fetchSequenceStats,
  fetchSequenceUsers,
}) => {
  const [filters, setFilters] = useState({
    day: undefined,
    completed: undefined,  // undefined = no filter, "true"/"false" = filtered
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    fetchSequenceStats();
    fetchSequenceUsers(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    fetchSequenceUsers(newFilters);
  };

  const handlePageChange = (page, pageSize) => {
    const newFilters = { ...filters, page, limit: pageSize };
    setFilters(newFilters);
    fetchSequenceUsers(newFilters);
  };

  // Funnel visualization data - uses "reached" for cumulative funnel
  const getFunnelData = () => {
    if (!sequence?.byDay || sequence.byDay.length === 0) return [];

    const days = [1, 2, 3, 4, 5];
    return days.map((day) => {
      const dayData = sequence.byDay.find((d) => d.day === day);
      return {
        day,
        label: `Dia ${day}`,
        reached: dayData?.reached || 0,  // Cumulative: users who reached this day
        current: dayData?.total || 0,    // Users currently AT this day
        pending: dayData?.pending || 0,
      };
    });
  };

  const funnelData = getFunnelData();
  const maxFunnelValue = Math.max(...funnelData.map((d) => d.reached), 1);

  // Table columns for users
  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      render: (email) => (
        <Tooltip title={email}>
          <Text ellipsis style={{ maxWidth: 200 }}>
            {email}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Dia Atual",
      dataIndex: "currentDay",
      key: "currentDay",
      width: 100,
      render: (day, record) => (
        <Tag color={record.completed ? "green" : "blue"}>
          {record.completed ? "Completo" : `Dia ${day}`}
        </Tag>
      ),
    },
    {
      title: "Proximo Envio",
      dataIndex: "nextSendAt",
      key: "nextSendAt",
      width: 150,
      render: (date, record) => {
        if (record.completed) return <Text type="secondary">-</Text>;
        if (!date) return <Text type="secondary">-</Text>;

        const nextDate = dayjs(date);
        const isOverdue = nextDate.isBefore(dayjs());

        return (
          <Tooltip title={nextDate.format("DD/MM/YYYY HH:mm")}>
            <Tag color={isOverdue ? "red" : "default"}>
              {isOverdue ? "Pendente" : nextDate.fromNow()}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Ultimo Envio",
      dataIndex: "lastSentAt",
      key: "lastSentAt",
      width: 140,
      render: (date) =>
        date ? dayjs(date).format("DD/MM/YY HH:mm") : "-",
    },
    {
      title: "Iniciado em",
      dataIndex: "startedAt",
      key: "startedAt",
      width: 140,
      render: (date) =>
        date ? dayjs(date).format("DD/MM/YY HH:mm") : "-",
    },
    {
      title: "Plano",
      dataIndex: "pplan",
      key: "pplan",
      width: 80,
      render: (pplan) => <Tag>{pplan || 0}</Tag>,
    },
  ];

  if (loading.sequence) {
    return (
      <Card title="Sequencia de Onboarding (5 dias)">
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <MailOutlined />
            <span>Sequencia de Emails de Onboarding</span>
          </Space>
        }
        extra={
          <Text type="secondary">
            Fluxo automatico de 5 emails apos signup
          </Text>
        }
        style={{ marginBottom: 24 }}
      >
        {/* Stats Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Total na Sequencia"
                value={sequence?.totalUsers || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Em Andamento"
                value={sequence?.activeSequences || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Completaram Tudo"
                value={sequence?.completedAll || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Pendentes (Atrasados)"
                value={sequence?.pendingNow || 0}
                prefix={<MailOutlined />}
                valueStyle={{ color: sequence?.pendingNow > 0 ? "#ff4d4f" : undefined }}
              />
            </Card>
          </Col>
        </Row>

        {/* Funnel Visualization */}
        <Title level={5} style={{ marginBottom: 16 }}>
          Funil de Onboarding
        </Title>
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[8, 8]} align="middle">
            {funnelData.map((item, index) => (
              <React.Fragment key={item.day}>
                <Col flex="1">
                  <Tooltip
                    title={
                      <div>
                        <div>Alcancaram este dia: {item.reached}</div>
                        <div>Atualmente neste dia: {item.current}</div>
                        {item.pending > 0 && <div>Pendentes: {item.pending}</div>}
                      </div>
                    }
                  >
                    <Card
                      size="small"
                      style={{
                        textAlign: "center",
                        background: item.reached > 0 ? "#f6ffed" : "#fafafa",
                        border: item.pending > 0 ? "1px solid #ff4d4f" : undefined,
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#666" }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: "bold", color: "#1890ff" }}>
                        {item.reached}
                      </div>
                      {item.current > 0 && item.current !== item.reached && (
                        <Tag color="blue" style={{ fontSize: 10 }}>
                          {item.current} aqui
                        </Tag>
                      )}
                      {item.pending > 0 && (
                        <Tag color="red" style={{ fontSize: 10 }}>
                          {item.pending} pendente{item.pending > 1 ? "s" : ""}
                        </Tag>
                      )}
                      <Progress
                        percent={Math.round((item.reached / maxFunnelValue) * 100)}
                        showInfo={false}
                        strokeColor="#1890ff"
                        size="small"
                      />
                    </Card>
                  </Tooltip>
                </Col>
                {index < funnelData.length - 1 && (
                  <Col>
                    <ArrowRightOutlined style={{ color: "#999" }} />
                  </Col>
                )}
              </React.Fragment>
            ))}
            <Col>
              <ArrowRightOutlined style={{ color: "#999" }} />
            </Col>
            <Col flex="1">
              <Card
                size="small"
                style={{
                  textAlign: "center",
                  background: "#f6ffed",
                  borderColor: "#52c41a",
                }}
              >
                <div style={{ fontSize: 12, color: "#666" }}>Completo</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#52c41a" }}>
                  {sequence?.completedAll || 0}
                </div>
                <Tag color="green">
                  {(sequence?.averageCompletion || 0).toFixed(1)}% taxa
                </Tag>
                <Progress
                  percent={Math.round(sequence?.averageCompletion || 0)}
                  showInfo={false}
                  strokeColor="#52c41a"
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* Users Table */}
        <Title level={5} style={{ marginBottom: 16 }}>
          Usuarios na Sequencia
        </Title>

        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="Filtrar por dia"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => handleFilterChange("day", value)}
            value={filters.day}
          >
            <Option value={1}>Dia 1</Option>
            <Option value={2}>Dia 2</Option>
            <Option value={3}>Dia 3</Option>
            <Option value={4}>Dia 4</Option>
            <Option value={5}>Dia 5</Option>
          </Select>
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => handleFilterChange("completed", value)}
            value={filters.completed}
          >
            <Option value="false">Em andamento</Option>
            <Option value="true">Completo</Option>
          </Select>
        </Space>

        {sequenceUsers?.users?.length > 0 ? (
          <Table
            columns={columns}
            dataSource={sequenceUsers.users}
            loading={loading.sequenceUsers}
            rowKey={(record) => record.email}
            size="small"
            pagination={{
              current: sequenceUsers.page,
              pageSize: sequenceUsers.limit,
              total: sequenceUsers.total,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} usuarios`,
              pageSizeOptions: ["10", "20", "50"],
              onChange: handlePageChange,
            }}
          />
        ) : (
          <Empty description="Nenhum usuario encontrado" />
        )}
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  sequence: state.emailAnalytics.sequence,
  sequenceUsers: state.emailAnalytics.sequenceUsers,
  loading: state.emailAnalytics.loading,
});

export default connect(mapStateToProps, {
  fetchSequenceStats,
  fetchSequenceUsers,
})(EmailSequenceSection);
